import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { useState } from 'react'

interface TaxRates {
  income: number
  corporate: number
  sales: number
  luxury: number
  property: number
  carbon: number
  digital: number
}

function TaxPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()
  const { token } = useAuthStore()
  const [taxes, setTaxes] = useState<TaxRates>({
    income: 25,
    corporate: 20,
    sales: 8,
    luxury: 15,
    property: 1.2,
    carbon: 5,
    digital: 3
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  const taxTypes = [
    { 
      key: 'income', 
      name: 'Income Tax', 
      description: 'Personal income taxation for individuals',
      maxRate: 60,
      revenueMultiplier: 0.8,
      approvalImpact: -0.5,
      unit: '%'
    },
    { 
      key: 'corporate', 
      name: 'Corporate Tax', 
      description: 'Business and corporate taxation',
      maxRate: 50,
      revenueMultiplier: 0.6,
      approvalImpact: -0.2,
      unit: '%'
    },
    { 
      key: 'sales', 
      name: 'Sales Tax', 
      description: 'Goods and services taxation (VAT/GST)',
      maxRate: 25,
      revenueMultiplier: 0.7,
      approvalImpact: -0.6,
      unit: '%'
    },
    { 
      key: 'luxury', 
      name: 'Luxury Tax', 
      description: 'High-end goods and luxury items',
      maxRate: 50,
      revenueMultiplier: 0.3,
      approvalImpact: 0.1,
      unit: '%'
    },
    { 
      key: 'property', 
      name: 'Property Tax', 
      description: 'Real estate and property taxation',
      maxRate: 5,
      revenueMultiplier: 0.4,
      approvalImpact: -0.4,
      unit: '%'
    },
    { 
      key: 'carbon', 
      name: 'Carbon Tax', 
      description: 'Environmental carbon emissions tax',
      maxRate: 30,
      revenueMultiplier: 0.2,
      approvalImpact: 0.3,
      unit: '%'
    },
    { 
      key: 'digital', 
      name: 'Digital Services Tax', 
      description: 'Technology and digital services taxation',
      maxRate: 15,
      revenueMultiplier: 0.4,
      approvalImpact: -0.1,
      unit: '%'
    }
  ]

  const handleTaxChange = (taxType: keyof TaxRates, value: number) => {
    const maxRate = taxTypes.find(t => t.key === taxType)?.maxRate || 100
    setTaxes(prev => ({
      ...prev,
      [taxType]: Math.max(0, Math.min(maxRate, value))
    }))
  }

  const calculateRevenue = (taxType: keyof TaxRates) => {
    const tax = taxTypes.find(t => t.key === taxType)
    if (!tax) return 0
    return taxes[taxType] * tax.revenueMultiplier * 1000000000 // Simplified calculation
  }

  const calculateApprovalImpact = (taxType: keyof TaxRates) => {
    const tax = taxTypes.find(t => t.key === taxType)
    if (!tax) return 0
    return taxes[taxType] * tax.approvalImpact
  }

  const totalRevenue = Object.keys(taxes).reduce((total, key) => {
    return total + calculateRevenue(key as keyof TaxRates)
  }, 0)

  const totalApprovalImpact = Object.keys(taxes).reduce((total, key) => {
    return total + calculateApprovalImpact(key as keyof TaxRates)
  }, 0)

  const submitTaxes = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/games/${gameId}/taxes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taxes)
      })

      const data = await response.json()
      if (data.success) {
        setMessage('Tax rates updated successfully!')
        setTimeout(() => navigate(`/game/${gameId}`), 2000)
      } else {
        setMessage(data.error || 'Failed to update tax rates')
      }
    } catch (error) {
      setMessage('Error submitting tax rates')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    if (amount >= 1e9) return `$${(amount / 1e9).toFixed(1)}B`
    if (amount >= 1e6) return `$${(amount / 1e6).toFixed(1)}M`
    return `$${amount.toFixed(0)}`
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Tax Policy Management</h1>
        <button 
          onClick={() => navigate(`/game/${gameId}`)}
          className="btn btn-secondary"
        >
          Back to Game
        </button>
      </div>

      {/* Tax Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Revenue Projection</h3>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-gray-600 mt-1">Estimated annual revenue from all taxes</p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Approval Impact</h3>
          <p className={`text-3xl font-bold ${totalApprovalImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalApprovalImpact > 0 ? '+' : ''}{totalApprovalImpact.toFixed(1)}%
          </p>
          <p className="text-sm text-gray-600 mt-1">Expected change in approval rating</p>
        </div>
      </div>

      {/* Tax Rate Management */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {taxTypes.map(taxType => (
          <div key={taxType.key} className="card">
            <div className="flex justify-between items-start mb-3">
              <h3 className="font-semibold text-gray-900">{taxType.name}</h3>
              <span className="text-lg font-bold text-gray-700">
                {taxes[taxType.key as keyof TaxRates].toFixed(1)}{taxType.unit}
              </span>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{taxType.description}</p>
            
            <div className="space-y-3">
              <input
                type="range"
                min="0"
                max={taxType.maxRate}
                step="0.1"
                value={taxes[taxType.key as keyof TaxRates]}
                onChange={(e) => handleTaxChange(taxType.key as keyof TaxRates, parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              
              <div className="flex justify-between items-center text-sm">
                <button
                  onClick={() => handleTaxChange(taxType.key as keyof TaxRates, Math.max(0, taxes[taxType.key as keyof TaxRates] - 0.5))}
                  className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                >
                  -0.5%
                </button>
                
                <input
                  type="number"
                  min="0"
                  max={taxType.maxRate}
                  step="0.1"
                  value={taxes[taxType.key as keyof TaxRates]}
                  onChange={(e) => handleTaxChange(taxType.key as keyof TaxRates, parseFloat(e.target.value) || 0)}
                  className="w-20 px-2 py-1 text-center border border-gray-300 rounded text-sm"
                />
                
                <button
                  onClick={() => handleTaxChange(taxType.key as keyof TaxRates, Math.min(taxType.maxRate, taxes[taxType.key as keyof TaxRates] + 0.5))}
                  className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                >
                  +0.5%
                </button>
              </div>
              
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Revenue: {formatCurrency(calculateRevenue(taxType.key as keyof TaxRates))}</span>
                  <span className={calculateApprovalImpact(taxType.key as keyof TaxRates) >= 0 ? 'text-green-600' : 'text-red-600'}>
                    Approval: {calculateApprovalImpact(taxType.key as keyof TaxRates) > 0 ? '+' : ''}
                    {calculateApprovalImpact(taxType.key as keyof TaxRates).toFixed(1)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ width: `${(taxes[taxType.key as keyof TaxRates] / taxType.maxRate) * 100}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message Display */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Submit Button */}
      <div className="card">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Apply Tax Policy Changes</h3>
            <p className="text-sm text-gray-600">
              This will update your tax policy for the current turn and affect government revenue and citizen approval.
            </p>
          </div>
          
          <button
            onClick={submitTaxes}
            disabled={loading}
            className={`px-6 py-3 font-semibold rounded-lg ${
              !loading
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {loading ? 'Applying...' : 'Apply Tax Changes'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TaxPage